import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { Maximize2 } from "lucide-react";

import { useAIAgentThread } from "@/AgentThread/AgentThreadSidebar/AIAgentThreadContext";
import { useNavigation } from "@/providers";
import { useCurrentOrgId } from "@/providers/auth/hooks";
import { ThreadVisibilityControl } from "@/shared/components/agentThread/ThreadVisibilityControl";
import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { type AgentThreadRecord } from "@/shared/domain/agentThread";
import { useActiveAgentThread } from "@/shared/domain/agentThread/hooks";

import styles from "./AgentThreadSidebarControls.module.css";
import { EarlyAccessInfoPopover } from "./EarlyAccessInfoPopover.tsx";

interface ActiveThreadVisibilityControlProps {
  thread: AgentThreadRecord | undefined;

  /** Whether the body has been swapped for the thread history list. */
  showHistory: boolean;
}

const ActiveThreadVisibilityControl = ({
  thread,
  showHistory,
}: ActiveThreadVisibilityControlProps) => {
  // Hidden rather than disabled on the new-thread view: a greyed-out lock
  // would read as "this thread is private" when there is no thread yet.
  if (thread === undefined) {
    return null;
  }

  // Opening history leaves `currentThreadId` set, so this control would go on
  // offering share and un-share for a thread that is no longer on screen —
  // one click from re-scoping the wrong thread, next to a list of the others.
  if (showHistory) {
    return null;
  }

  return <ThreadVisibilityControl thread={thread} variant="icon" />;
};

interface AgentThreadSidebarControlsProps {
  onClose: () => void;

  /** Whether the body has been swapped for the thread history list. Part of
   *  the collaboration bar's address, not the thread provider's state. */
  showHistory: boolean;

  /** Ask the bar to show the history pane, or return to the conversation. */
  onShowHistory: (showHistory: boolean) => void;
}

export const AgentThreadSidebarControls = ({
  onClose,
  showHistory,
  onShowHistory,
}: AgentThreadSidebarControlsProps) => {
  const orgId = useCurrentOrgId();
  const { goto } = useNavigation();
  const { currentThreadId, handleNewThread } = useAIAgentThread();

  // `loadMessages` is part of the query key, so `true` reuses the cache entry
  // the sidebar's AgentThread mounts rather than fetching the thread twice.
  // This header reads only the thread's visibility.
  const { data: activeThread } = useActiveAgentThread(currentThreadId, {
    orgId,
    loadMessages: true,
  });

  // Closing the sidebar behind the navigation: two open panes on one thread
  // would both accept messages.
  const handleOpenInThreadView = () => {
    if (currentThreadId === undefined) {
      return;
    }
    goto.agentThread({ threadId: currentThreadId });
    onClose();
  };

  return (
    <header className={styles.header}>
      <div className={styles.titleContainer}>
        <EarlyAccessInfoPopover />
      </div>
      <div className={styles.navItems}>
        <ActiveThreadVisibilityControl
          thread={activeThread}
          showHistory={showHistory}
        />
        <RobotoTooltip
          title={showHistory ? "Hide My Threads" : "Show My Threads"}
        >
          <span>
            <RobotoIconButton
              onClick={() => onShowHistory(!showHistory)}
              eventName={"AgentThreadHistoryToggled"}
              size="small"
            >
              {showHistory ? (
                <LibraryBooksIcon className={styles.activeIconButton} />
              ) : (
                <LibraryBooksIcon className={styles.iconButton} />
              )}
            </RobotoIconButton>
          </span>
        </RobotoTooltip>
        <RobotoTooltip title={"New Thread"}>
          <span>
            <RobotoIconButton
              // Leaves the history pane too: asking for a new thread is one
              // navigation, and the sidebar's answer to it names the
              // conversation pane.
              onClick={handleNewThread}
              eventName={"NewAgentThreadClicked"}
              size="small"
            >
              <AddIcon className={styles.iconButton} />
            </RobotoIconButton>
          </span>
        </RobotoTooltip>
        <RobotoTooltip
          title={
            currentThreadId === undefined
              ? "Start a thread to open it in the thread view"
              : "Open in thread view"
          }
        >
          {/* A disabled button fires no pointer events, so the tooltip
              listens on the span instead. */}
          <span>
            <RobotoIconButton
              onClick={handleOpenInThreadView}
              eventName={"AgentThreadOpenedInThreadView"}
              size="small"
              disabled={currentThreadId === undefined}
              // MUI puts the tooltip's text on the span, not the button, and
              // that text changes with the disabled state; the button needs a
              // name of its own that does not.
              aria-label="Open in thread view"
            >
              <Maximize2 className={styles.iconButton} />
            </RobotoIconButton>
          </span>
        </RobotoTooltip>
        <RobotoTooltip title={"Close"}>
          <span>
            <RobotoIconButton
              onClick={onClose}
              eventName={"CloseAgentThreadWindowClose"}
              size="small"
            >
              <CloseIcon className={styles.iconButton} />
            </RobotoIconButton>
          </span>
        </RobotoTooltip>
      </div>
    </header>
  );
};
